import type MarkdownIt from 'markdown-it';
import type StateCore from 'markdown-it/lib/rules_core/state_core.mjs';
import type Token from 'markdown-it/lib/token.mjs';

import type { EntityAnnotationEntity } from '../../entity-annotations';
import {
	ENTITY_ANNOTATION_EXTENSION_TYPE,
	ENTITY_ANNOTATION_PERSON_LOCAL_ID_PREFIX,
	INFERRED_PROJECT_EXTENSION_KEY,
	isEntityAnnotationEntity,
} from '../../entity-annotations';
import entityAnnotationMarkers from './markers';
import stripEntityAnnotationMarkers from './strip-entity-annotation-markers';

const MARKER_START = entityAnnotationMarkers.start;
const MARKER_END = entityAnnotationMarkers.end;
const ENTITY_MARKER_END = entityAnnotationMarkers.endMarker;

const nonBodyBlockTypes = new Set(['caption', 'heading', 'tableHeader']);

const parseEntity = (value: string): EntityAnnotationEntity | undefined => {
	try {
		const entity: unknown = JSON.parse(decodeURIComponent(value));
		return isEntityAnnotationEntity(entity) ? entity : undefined;
	} catch {
		return undefined;
	}
};

const createTextToken = (state: StateCore, content: string, level: number): Token => {
	const token = new state.Token('text', '', 0);
	token.content = content;
	token.level = level;
	return token;
};

const formatMarkOpenTypes = new Set(['em_open', 's_open', 'strong_open']);

const createMarkBoundaryToken = (state: StateCore, source: Token, nesting: 1 | -1): Token => {
	const type = source.type.replace(/_(?:open|close)$/, nesting === 1 ? '_open' : '_close');
	const token = new state.Token(type, source.tag, nesting);
	token.level = source.level;
	token.markup = source.markup;
	return token;
};

const getAccountIdFromIdentityAri = (ari: string): string | undefined =>
	ari.match(/^ari:cloud:identity::user\/([^/]+)$/)?.[1];

const createEntityAnnotationToken = (
	state: StateCore,
	entity: EntityAnnotationEntity,
	level: number,
	personAnnotationSequence: number,
): Token | undefined => {
	if (entity.type === 'Person') {
		const accountId = getAccountIdFromIdentityAri(entity.ari);
		if (!accountId) {
			return undefined;
		}

		const token = new state.Token('mention', '', 0);
		token.block = false;
		token.level = level;
		token.attrs = [
			['id', accountId],
			['localId', `${ENTITY_ANNOTATION_PERSON_LOCAL_ID_PREFIX}${personAnnotationSequence}`],
			['text', `@${entity.mention}`],
		];
		return token;
	}

	const token = new state.Token('entityAnnotation', '', 0);
	token.block = false;
	token.level = level;
	token.attrs = [
		['extensionType', ENTITY_ANNOTATION_EXTENSION_TYPE],
		['extensionKey', INFERRED_PROJECT_EXTENSION_KEY],
		[
			'parameters',
			JSON.stringify({
				name: entity.canonicalName,
				ari: entity.ari,
				canonicalName: entity.canonicalName,
				confidenceScore: entity.confidenceScore,
			}),
		],
		['text', entity.canonicalName],
	];
	return token;
};

const replaceEntityMarkers = ({
	children,
	isEligibleBlock,
	nextPersonAnnotationSequence,
	state,
}: {
	children: readonly Token[];
	isEligibleBlock: boolean;
	nextPersonAnnotationSequence: () => number;
	state: StateCore;
}): Token[] => {
	const result: Token[] = [];
	const activeFormatMarks: Token[] = [];
	let linkDepth = 0;

	for (const child of children) {
		if (formatMarkOpenTypes.has(child.type)) {
			activeFormatMarks.push(child);
			result.push(child);
			continue;
		}

		if (
			child.type.endsWith('_close') &&
			activeFormatMarks[activeFormatMarks.length - 1]?.type ===
				child.type.replace('_close', '_open')
		) {
			activeFormatMarks.pop();
			result.push(child);
			continue;
		}

		if (child.type === 'link_open') {
			linkDepth += 1;
			result.push(child);
			continue;
		}

		if (child.type === 'link_close') {
			linkDepth -= 1;
			result.push(child);
			continue;
		}

		if (child.type !== 'text') {
			if (child.content.includes(MARKER_START)) {
				child.content = stripEntityAnnotationMarkers(child.content);
			}
			result.push(child);
			continue;
		}

		if (!child.content.includes(MARKER_START)) {
			result.push(child);
			continue;
		}

		const markerPattern = new RegExp(
			`${MARKER_START}start:([^${MARKER_END}]*)${MARKER_END}([\\s\\S]*?)${ENTITY_MARKER_END}`,
			'g',
		);
		let cursor = 0;
		let match = markerPattern.exec(child.content);
		while (match) {
			if (match.index > cursor) {
				result.push(createTextToken(state, child.content.slice(cursor, match.index), child.level));
			}

			const entity = parseEntity(match[1]);
			const mention = match[2];
			const annotationToken = entity
				? createEntityAnnotationToken(state, entity, child.level, nextPersonAnnotationSequence())
				: undefined;
			const canConvert =
				entity &&
				entity.mention.toLowerCase() === mention.toLowerCase() &&
				isEligibleBlock &&
				linkDepth === 0;
			if (canConvert && annotationToken) {
				for (const mark of [...activeFormatMarks].reverse()) {
					result.push(createMarkBoundaryToken(state, mark, -1));
				}
				result.push(annotationToken);
				for (const mark of activeFormatMarks) {
					result.push(createMarkBoundaryToken(state, mark, 1));
				}
			} else {
				result.push(createTextToken(state, mention, child.level));
			}

			cursor = match.index + match[0].length;
			match = markerPattern.exec(child.content);
		}

		if (cursor < child.content.length) {
			result.push(
				createTextToken(
					state,
					stripEntityAnnotationMarkers(child.content.slice(cursor)),
					child.level,
				),
			);
		}
	}

	return result;
};

const entityAnnotations = (state: StateCore): void => {
	const openBlocks: string[] = [];
	let personAnnotationSequence = 0;

	for (const token of state.tokens) {
		if (token.type.endsWith('_open')) {
			openBlocks.push(token.type.slice(0, -'_open'.length));
		}

		if (token.type === 'fence' || token.type === 'code_block') {
			token.content = stripEntityAnnotationMarkers(token.content);
		}

		if (token.type === 'inline' && token.children?.length) {
			token.children = replaceEntityMarkers({
				children: token.children,
				isEligibleBlock: !openBlocks.some((type) => nonBodyBlockTypes.has(type)),
				nextPersonAnnotationSequence: () => personAnnotationSequence++,
				state,
			});
		}

		if (token.type.endsWith('_close')) {
			openBlocks.pop();
		}
	}
};

export default function mdPluginEntityAnnotations(md: MarkdownIt): void {
	md.core.ruler.push('entity-annotations', entityAnnotations);
}
